import React, { useEffect, useMemo, useRef, useState } from "react";
import SnSwitch from "@shared/components/SnSwitch";
import {
  Act,
  Project,
  toDate,
  fmt,
  diffDays,
  addDays,
  todayStr,
  pad,
  STATUS_COLOR,
  calcStatus,
  asOf,
  aggStatus,
  rollup,
  projectAgg,
} from "./wbsCalc";

/**
 * WBS현황 간트 (ISS2026100053) — 데이터 주입식 전용 컴포넌트
 *  - 시간축 매핑(주/월/년) + 월 눈금 헤더
 *  - 계획(남색)/실적(초록) 2줄 막대 + 지연(빨강) 4-Case 오버레이
 *  - 좌측 트리(프로젝트 > 대표ACT > 세부ACT) + 계획/실적(가중치 점수)/달성률/상태
 *  - 기준일 선, 전체펼침/닫기, 조회조건(프로젝트/상태) 필터
 *  상태·진척 로직은 wbsCalc 공유(목록 탭과 동일).
 */
export type { Act, Project } from "./wbsCalc";

const C_PLAN = "#1e40af";
const C_ACT = "#16a34a";
const C_DELAY = "#dc2626";

// 하우스 그리드 톤
const H_BG = "#eef3fb"; // 헤더/프로젝트행 연한 파랑
const H_BORDER = "#d6e0f5"; // 구분선
const H_TEXT = "#1e40af"; // 헤더 글자(blue)
const CELL_BORDER = "#eef1f6"; // 행 하단선
const PILL =
  "rounded-full border border-[#c3d4f0] bg-white text-[#2563eb] px-3 py-[2px] text-xs hover:bg-[#eef4ff]";

type View = "week" | "month" | "year";
const DAY_WIDTH: Record<View, number> = { week: 26, month: 9, year: 3 };
const ROW_H = 34;
const LEFT_W = 360;

export type BarKind = "plan" | "work"; // 계획막대 / 실적막대

type Row = {
  key: string;
  pjtId: string;
  depth: 0 | 1 | 2;
  name: string;
  ps: string;
  pe: string;
  as?: string;
  ae?: string;
  plan: number;
  act: number;
  achieve: number;
  status: string;
  expandable: boolean;
  expanded: boolean;
  onToggle?: () => void;
  movable: boolean; // 계획막대 드래그 가능(대표/프로젝트 제외, 리프만)
};

const WbsGantt: React.FC<{
  projects: Project[];
  filterProjectId?: string;
  filterStatus?: string;
  selectedProjectId?: string;
  onSelectProject?: (cdPjt: string) => void;
  onEditProject?: (cdPjt: string) => void; // 프로젝트 행 더블클릭 → 수정 팝업
  // bar: "plan" 계획막대 / "work" 실적막대. 실적 진행 중이면 pe 는 "" (종료일 없음)
  onMove?: (
    cdPjt: string,
    noLine: string,
    ps: string,
    pe: string,
    bar: BarKind
  ) => void;
}> = ({
  projects,
  filterProjectId,
  filterStatus,
  selectedProjectId,
  onSelectProject,
  onEditProject,
  onMove,
}) => {
  // 주/년 숨김 — 월 기준 고정 (필요 시 뷰 토글 복구)
  const [base, setBase] = useState<string>(todayStr());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // 계획·실적막대 드래그(좌우 이동). dragKey = `${행키}|${bar}`
  const dragRef = useRef<{
    key: string;
    bar: BarKind;
    startX: number;
    ps: string;
    pe: string;
    dx: number;
  } | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragDx, setDragDx] = useState(0);

  // 최초 1회만 전체 펼침. 재조회(막대 이동 저장 등)로 projects 가 바뀌어도
  // 사용자의 펼침/접힘 상태를 유지한다(id 안정). 새로 등장한 프로젝트/하위ACT만 펼침 추가.
  const seenPjtRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (projects.length === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      projects.forEach((p) => {
        if (!seenPjtRef.current.has(p.id)) {
          seenPjtRef.current.add(p.id);
          next.add(p.id);
          p.acts.forEach((a) => a.children?.length && next.add(a.id));
        }
      });
      return next;
    });
  }, [projects]);

  const dw = DAY_WIDTH.month;

  // 조회조건(프로젝트/상태) 클라이언트 필터 — 상태는 계산값
  const shown = useMemo(
    () =>
      projects.filter((p) => {
        if (!p.acts.length) return false;
        if (filterProjectId && p.id !== filterProjectId) return false;
        if (filterStatus) {
          const st = aggStatus(p.acts.map((a) => rollup(a, base).status));
          if (st !== filterStatus) return false;
        }
        return true;
      }),
    [projects, filterProjectId, filterStatus, base]
  );

  const { axisStart, totalDays } = useMemo(() => {
    const all: string[] = [base];
    shown.forEach((p) =>
      p.acts.forEach((a) => {
        const walk = (x: Act) => {
          if (x.ps) all.push(x.ps);
          if (x.pe) all.push(x.pe);
          if (x.as) all.push(x.as);
          if (x.ae) all.push(x.ae);
          x.children?.forEach(walk);
        };
        walk(a);
      })
    );
    const min = all.reduce((m, d) => (d < m ? d : m), all[0]);
    const max = all.reduce((m, d) => (d > m ? d : m), all[0]);
    const start = fmt(new Date(toDate(min).getTime() - 5 * 86400000));
    const end = fmt(new Date(toDate(max).getTime() + 10 * 86400000));
    return { axisStart: start, totalDays: diffDays(start, end) + 1 };
  }, [shown, base]);

  const x = (d: string) => diffDays(axisStart, d) * dw;
  const timelineW = totalDays * dw;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const expandAll = () => {
    const ids = new Set<string>();
    shown.forEach((p) => {
      ids.add(p.id);
      p.acts.forEach((a) => a.children?.length && ids.add(a.id));
    });
    setExpanded(ids);
  };
  const collapseAll = () => setExpanded(new Set());

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    shown.forEach((p) => {
      if (!p.acts.length) return;
      const pr = projectAgg(p, base);
      const pPs = p.acts.reduce((m, a) => (a.ps < m ? a.ps : m), p.acts[0].ps);
      const pPe = p.acts.reduce((m, a) => (a.pe > m ? a.pe : m), p.acts[0].pe);
      out.push({
        key: p.id,
        pjtId: p.id,
        depth: 0,
        name: p.name,
        ps: pPs,
        pe: pPe,
        plan: pr.plan,
        act: pr.act,
        achieve: pr.plan ? Math.round((pr.act / pr.plan) * 100) : 0,
        status: pr.status,
        expandable: true,
        expanded: expanded.has(p.id),
        onToggle: () => toggle(p.id),
        movable: false,
      });
      if (!expanded.has(p.id)) return;
      p.acts.forEach((a) => {
        const r = rollup(a, base);
        const hasKids = !!a.children?.length;
        out.push({
          key: a.id,
          pjtId: p.id,
          depth: 1,
          name: a.name,
          ps: a.ps,
          pe: a.pe,
          as: a.as,
          ae: a.ae,
          plan: r.plan,
          act: r.act,
          achieve: r.plan ? Math.round((r.act / r.plan) * 100) : 0,
          status: r.status,
          expandable: hasKids,
          expanded: expanded.has(a.id),
          onToggle: hasKids ? () => toggle(a.id) : undefined,
          movable: !hasKids,
        });
        if (hasKids && expanded.has(a.id)) {
          a.children!.forEach((c) => {
            const cr = rollup(c, base);
            out.push({
              key: c.id,
              pjtId: p.id,
              depth: 2,
              name: c.name,
              ps: c.ps,
              pe: c.pe,
              as: c.as,
              ae: c.ae,
              plan: cr.plan,
              act: cr.act,
              achieve: cr.plan ? Math.round((cr.act / cr.plan) * 100) : 0,
              status: cr.status,
              expandable: false,
              expanded: false,
              movable: true,
            });
          });
        }
      });
    });
    return out;
  }, [shown, expanded, base]);

  const resetDrag = () => {
    dragRef.current = null;
    setDragKey(null);
    setDragDx(0);
  };
  // 이동 저장 후 재조회 결과가 오면 임시 위치(translate)를 푼다
  useEffect(resetDrag, [projects]);

  const onBarDown = (e: React.PointerEvent, r: Row, bar: BarKind) => {
    if (!r.movable || !onMove || e.button !== 0) return;
    const ps = bar === "work" ? r.as : r.ps;
    const pe = bar === "work" ? r.ae || "" : r.pe;
    if (!ps || (bar === "plan" && !pe)) return;
    // 더블클릭으로 잡힌 텍스트 선택 위에서 누르면 브라우저 기본 드래그가 pointercancel 을 내고
    // pointerup 이 오지 않아 드래그 상태가 남는다 → 기본 동작(선택/네이티브 드래그) 차단
    e.preventDefault();
    window.getSelection()?.removeAllRanges();
    dragRef.current = { key: r.key, bar, startX: e.clientX, ps, pe, dx: 0 };
    setDragKey(`${r.key}|${bar}`);
    setDragDx(0);
    (e.target as Element).setPointerCapture?.(e.pointerId);
    e.stopPropagation();
  };
  const onBarMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    if (e.buttons === 0) return resetDrag(); // 버튼을 뗀 상태(up 유실) — 이동하지 않고 취소
    const dx = e.clientX - dragRef.current.startX;
    dragRef.current.dx = dx;
    setDragDx(dx);
  };
  const onBarUp = () => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    const days = Math.round(d.dx / dw);
    const [cdPjt, noLine] = d.key.split("#");
    if (days !== 0 && onMove && cdPjt && noLine) {
      setDragDx(days * dw); // 재조회 전까지 놓은 칸에 고정(원위치로 튀지 않게)
      onMove(
        cdPjt,
        noLine,
        addDays(d.ps, days),
        d.pe ? addDays(d.pe, days) : "",
        d.bar
      );
    } else {
      resetDrag();
    }
  };
  // 캡처 유실/취소 — 이동 없이 원위치
  const onBarCancel = () => dragRef.current && resetDrag();

  const renderBars = (r: Row) => {
    if (!r.ps || !r.pe) return null;
    const st = calcStatus(r.ps, r.pe, r.as, r.ae, base);
    // 실적 막대·지연은 기준일 시점까지만 (기준일 이후 실적은 아직 없는 것으로)
    const { as: rAs, ae: rAe } = asOf(r.as, r.ae, base);
    const plan = {
      left: x(r.ps),
      width: Math.max((diffDays(r.ps, r.pe) + 1) * dw, 2),
    };
    const actEnd = rAe || (rAs ? base : undefined);
    const act =
      rAs && actEnd
        ? {
            left: x(rAs),
            width: Math.max((diffDays(rAs, actEnd) + 1) * dw, 2),
          }
        : null;
    const startDelay = st.lateStart
      ? // 착수 전이면 기준일 당일까지(기준선까지), 착수했으면 실적시작 전날까지
        { left: x(r.ps), width: Math.max((rAs ? diffDays(r.ps, rAs) : diffDays(r.ps, base) + 1) * dw, 2) }
      : null;
    const endDelay = st.lateEnd
      ? {
          left: x(r.pe) + dw,
          width: Math.max(diffDays(r.pe, rAe || base) * dw, 2),
        }
      : null;
    const draggingPlan = dragKey === `${r.key}|plan`;
    const draggingWork = dragKey === `${r.key}|work`;
    return (
      <>
        <div
          onPointerDown={r.movable ? (e) => onBarDown(e, r, "plan") : undefined}
          onPointerMove={r.movable ? onBarMove : undefined}
          onPointerUp={r.movable ? onBarUp : undefined}
          onPointerCancel={r.movable ? onBarCancel : undefined}
          onLostPointerCapture={r.movable ? onBarCancel : undefined}
          onClick={r.movable ? (e) => e.stopPropagation() : undefined}
          onDoubleClick={r.movable ? (e) => e.stopPropagation() : undefined}
          title={r.movable ? "드래그하여 계획일정 이동" : undefined}
          style={{
            position: "absolute",
            left: plan.left,
            top: 5,
            width: plan.width,
            height: 9,
            background: C_PLAN,
            borderRadius: 2,
            opacity: 0.9,
            cursor: r.movable ? "grab" : "default",
            transform: draggingPlan ? `translateX(${dragDx}px)` : undefined,
            touchAction: "none",
          }}
        />
        {act && (
          <div
            onPointerDown={
              r.movable ? (e) => onBarDown(e, r, "work") : undefined
            }
            onPointerMove={r.movable ? onBarMove : undefined}
            onPointerUp={r.movable ? onBarUp : undefined}
            onPointerCancel={r.movable ? onBarCancel : undefined}
            onLostPointerCapture={r.movable ? onBarCancel : undefined}
            onClick={r.movable ? (e) => e.stopPropagation() : undefined}
            onDoubleClick={r.movable ? (e) => e.stopPropagation() : undefined}
            title={r.movable ? "드래그하여 실적일정 이동" : undefined}
            style={{
              position: "absolute",
              left: act.left,
              top: 19,
              width: act.width,
              height: 9,
              background: C_ACT,
              borderRadius: 2,
              cursor: r.movable ? "grab" : "default",
              transform: draggingWork ? `translateX(${dragDx}px)` : undefined,
              touchAction: "none",
            }}
          />
        )}
        {startDelay && (
          <div
            style={{
              position: "absolute",
              left: startDelay.left,
              top: 19,
              width: startDelay.width,
              height: 9,
              background: C_DELAY,
              borderRadius: 2,
              opacity: 0.9,
              pointerEvents: "none",
            }}
          />
        )}
        {endDelay && (
          <div
            style={{
              position: "absolute",
              left: endDelay.left,
              top: 19,
              width: endDelay.width,
              height: 9,
              background: C_DELAY,
              borderRadius: 2,
              opacity: 0.9,
              pointerEvents: "none",
            }}
          />
        )}
      </>
    );
  };

  const monthTicks = useMemo(() => {
    const ticks: { left: number; label: string }[] = [];
    let d = toDate(axisStart);
    d = new Date(d.getFullYear(), d.getMonth(), 1);
    for (let i = 0; i < 60; i++) {
      const left = x(fmt(d));
      if (left > timelineW) break;
      ticks.push({
        left,
        label: `${d.getFullYear()}.${pad(d.getMonth() + 1)}`,
      });
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
    return ticks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axisStart, dw, timelineW]);

  const Badge = ({ s }: { s: string }) => (
    <span
      style={{
        background: STATUS_COLOR[s] || "#9ca3af",
        color: "#fff",
        borderRadius: 4,
        padding: "1px 6px",
        fontSize: 11,
      }}
    >
      {s}
    </span>
  );

  return (
    <div
      className="h-full flex flex-col"
      style={{
        border: "1px solid rgb(186,191,199)",
        borderRadius: 0, // 4모서리 전부 각지게
        overflow: "hidden",
        background: "#fff",
      }}
    >
      <div className="flex items-center gap-2 px-2 py-1.5 border-b bg-white text-sm">
        <label className="text-gray-500">기준일</label>
        <input
          type="date"
          value={`${base.slice(0, 4)}-${base.slice(4, 6)}-${base.slice(6, 8)}`}
          onChange={(e) =>
            e.target.value && setBase(e.target.value.replace(/-/g, ""))
          }
          className="border border-slate-300 rounded px-2 py-0.5"
        />
        <SnSwitch
          labelCheck="전체 펼침"
          labelUncheck="전체 닫기"
          defaultChecked
          onChange={(_e: any, checked: boolean) =>
            checked ? expandAll() : collapseAll()
          }
        />
        <span className="ml-auto flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <i
              style={{
                width: 14,
                height: 9,
                background: C_PLAN,
                display: "inline-block",
                borderRadius: 2,
              }}
            />
            계획
          </span>
          <span className="flex items-center gap-1">
            <i
              style={{
                width: 14,
                height: 9,
                background: C_ACT,
                display: "inline-block",
                borderRadius: 2,
              }}
            />
            실적
          </span>
          <span className="flex items-center gap-1">
            <i
              style={{
                width: 14,
                height: 9,
                background: C_DELAY,
                display: "inline-block",
                borderRadius: 2,
              }}
            />
            지연
          </span>
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
          조회된 WBS 데이터가 없습니다.
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <div style={{ width: LEFT_W + timelineW, position: "relative" }}>
            <div
              className="flex"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 3,
                background: H_BG,
                borderBottom: `1px solid ${H_BORDER}`,
              }}
            >
              <div
                className="flex items-center"
                style={{
                  position: "sticky",
                  left: 0,
                  zIndex: 4,
                  width: LEFT_W,
                  height: 30,
                  background: H_BG,
                  borderRight: `1px solid ${H_BORDER}`,
                }}
              >
                <div
                  style={{ flex: 1, color: H_TEXT }}
                  className="px-2 text-xs font-semibold"
                >
                  프로젝트 / Activity
                </div>
                <div
                  style={{
                    width: 48,
                    color: H_TEXT,
                    borderLeft: `1px solid ${H_BORDER}`,
                  }}
                  className="text-xs text-center font-semibold"
                >
                  계획
                </div>
                <div
                  style={{
                    width: 48,
                    color: H_TEXT,
                    borderLeft: `1px solid ${H_BORDER}`,
                  }}
                  className="text-xs text-center font-semibold"
                >
                  실적
                </div>
                <div
                  style={{
                    width: 52,
                    color: H_TEXT,
                    borderLeft: `1px solid ${H_BORDER}`,
                  }}
                  className="text-xs text-center font-semibold"
                >
                  달성률
                </div>
                <div
                  style={{
                    width: 52,
                    color: H_TEXT,
                    borderLeft: `1px solid ${H_BORDER}`,
                  }}
                  className="text-xs text-center font-semibold"
                >
                  상태
                </div>
              </div>
              <div
                style={{ position: "relative", width: timelineW, height: 30 }}
              >
                {monthTicks.map((t) => (
                  <div
                    key={t.left}
                    style={{
                      position: "absolute",
                      left: t.left,
                      top: 0,
                      height: 30,
                      borderLeft: `1px solid ${H_BORDER}`,
                    }}
                  >
                    <span
                      className="text-[11px] pl-1"
                      style={{ color: H_TEXT }}
                    >
                      {t.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                // 기준일 당일까지 경과로 계산(planPct·실적 막대 +1일)하므로 선은 기준일 칸의 끝에 둔다
                left: LEFT_W + x(base) + dw - 1,
                top: 30,
                width: 2,
                height: rows.length * ROW_H,
                background: "#f59e0b",
                zIndex: 2,
              }}
            />

            {rows.map((r) => {
              const sel = !!selectedProjectId && r.pjtId === selectedProjectId;
              const leftBg = sel ? "#dbeafe" : r.depth === 0 ? H_BG : "#fff";
              return (
                <div
                  key={r.key}
                  className="flex"
                  style={{
                    height: ROW_H,
                    borderBottom: `1px solid ${CELL_BORDER}`,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => onSelectProject?.(r.pjtId)}
                  onDoubleClick={
                    r.depth === 0 ? () => onEditProject?.(r.pjtId) : undefined
                  }
                  title={
                    r.depth === 0
                      ? "클릭: WBS 등록 대상 선택 · 더블클릭: 프로젝트 수정"
                      : "클릭하면 이 프로젝트가 WBS 등록 대상으로 선택됩니다"
                  }
                >
                  <div
                    className="flex items-center"
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      width: LEFT_W,
                      background: leftBg,
                      borderRight: `1px solid ${H_BORDER}`,
                      boxShadow: sel ? "inset 3px 0 0 #2563eb" : undefined,
                    }}
                  >
                    <div
                      style={{ flex: 1, paddingLeft: 8 + r.depth * 16 }}
                      className="truncate text-sm flex items-center gap-1"
                    >
                      {r.expandable ? (
                        <button
                          onClick={r.onToggle}
                          className="text-gray-400 w-4"
                        >
                          {r.expanded ? "▾" : "▸"}
                        </button>
                      ) : (
                        <span className="w-4" />
                      )}
                      <span
                        className={
                          r.depth === 0
                            ? "font-semibold text-slate-700"
                            : "text-slate-600"
                        }
                      >
                        {r.name}
                      </span>
                    </div>
                    <div
                      style={{
                        width: 48,
                        borderLeft: `1px solid ${CELL_BORDER}`,
                      }}
                      className="text-xs text-center text-gray-600"
                    >
                      {r.plan}
                    </div>
                    <div
                      style={{
                        width: 48,
                        borderLeft: `1px solid ${CELL_BORDER}`,
                      }}
                      className="text-xs text-center text-gray-600"
                    >
                      {r.act}
                    </div>
                    <div
                      style={{
                        width: 52,
                        borderLeft: `1px solid ${CELL_BORDER}`,
                      }}
                      className="text-xs text-center text-gray-600"
                    >
                      {r.achieve}%
                    </div>
                    <div
                      style={{
                        width: 52,
                        borderLeft: `1px solid ${CELL_BORDER}`,
                      }}
                      className="text-center"
                    >
                      <Badge s={r.status} />
                    </div>
                  </div>
                  <div style={{ position: "relative", width: timelineW }}>
                    {monthTicks.map((t) => (
                      <div
                        key={t.left}
                        style={{
                          position: "absolute",
                          left: t.left,
                          top: 0,
                          height: ROW_H,
                          borderLeft: "1px solid #f8fafc",
                        }}
                      />
                    ))}
                    {renderBars(r)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WbsGantt;
